import React, { useState, useEffect } from 'react';

import { Input, Icon, Button, Card, CardHeader, CardBody } from '@repo/ui';
import type {
  ArchivalConfiguration,
  ArchivalServiceType,
  CreateArchivalConfigDto,
  ArchivalServiceInfo,
} from '@xtnd/web/features/archival/types/archivalType';
import {
  useCreateConfiguration,
  useUpdateConfiguration,
  useTestConfiguration,
} from '@xtnd/web/features/archival/queries/useArchivalQuery';

interface CreateArchivalConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  config?: ArchivalConfiguration | null;
  supportedServices: ArchivalServiceInfo[];
  onSuccess: () => void;
}

interface FormData {
  serviceType: ArchivalServiceType;

  // Azure Blob Storage fields
  storageAccount?: string;
  containerName?: string;
  sasToken?: string;

  // AWS S3 fields
  bucketName?: string;
  region?: string;
  accessKeyId?: string;
  secretAccessKey?: string;

  // OneDrive fields
  folderPath?: string;
  clientId?: string;
  clientSecret?: string;
  refreshToken?: string;
}

/**
 * Create/Edit Archival Configuration Modal
 *
 * Provides forms for configuring external archival services:
 * - Azure Blob Storage (SAS token authentication)
 * - AWS S3 (IAM credentials)
 * - OneDrive (OAuth2 credentials)
 */
export const CreateArchivalConfigModal: React.FC<CreateArchivalConfigModalProps> = ({
  isOpen,
  onClose,
  config,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormData>({
    serviceType: 'azure-blob',
  });
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isTestingConfig, setIsTestingConfig] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const isEditing = !!config;

  // Use the custom hooks
  const createConfigMutation = useCreateConfiguration();
  const updateConfigMutation = useUpdateConfiguration();
  const testConfigMutation = useTestConfiguration(config?.serviceId);

  // Determine which mutation to use
  const createOrUpdateMutation = isEditing ? updateConfigMutation : createConfigMutation;

  // Initialize form data when modal opens or config changes
  useEffect(() => {
    if (isOpen) {
      if (config) {
        // Edit mode - populate form with existing config
        setFormData({
          serviceType: config.serviceType,
          // Populate non-sensitive fields and show placeholders for sensitive fields
          ...(config.serviceType === 'azure-blob' && {
            storageAccount: (config.config as any).storageAccountName,
            containerName: (config.config as any).containerName,
            sasToken: (config.config as any).sasToken?.ssmPath ? '••••••••••••••••' : '', // Show placeholder if SSM path exists
          }),
          ...(config.serviceType === 's3' && {
            bucketName: (config.config as any).bucketName,
            region: (config.config as any).region,
            accessKeyId: (config.config as any).accessKeyId?.ssmPath ? '••••••••••••••••' : '',
            secretAccessKey: (config.config as any).secretAccessKey?.ssmPath
              ? '••••••••••••••••'
              : '',
          }),
          ...(config.serviceType === 'onedrive' && {
            folderPath: (config.config as any).folderPath,
            clientId: (config.config as any).clientId?.ssmPath ? '••••••••••••••••' : '',
            clientSecret: (config.config as any).clientSecret?.ssmPath ? '••••••••••••••••' : '',
            refreshToken: (config.config as any).refreshToken?.ssmPath ? '••••••••••••••••' : '',
          }),
        });
      } else {
        // Create mode - reset form
        setFormData({
          serviceType: 'azure-blob',
        });
      }
      setValidationErrors({});
      setTestResult(null);
      setShowSecrets({});
    }
  }, [isOpen, config]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Clear test result when form changes
    setTestResult(null);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Service ID is auto-generated, no validation needed

    // Validate based on service type
    if (formData.serviceType === 'azure-blob') {
      if (!formData.storageAccount?.trim()) {
        errors.storageAccount = 'Storage account is required';
      }
      if (!formData.containerName?.trim()) {
        errors.containerName = 'Container name is required';
      }
      if (!formData.sasToken?.trim() && !isEditing) {
        errors.sasToken = 'SAS token is required';
      }
    } else if (formData.serviceType === 's3') {
      if (!formData.bucketName?.trim()) {
        errors.bucketName = 'Bucket name is required';
      }
      if (!formData.region?.trim()) {
        errors.region = 'Region is required';
      }
      if (!formData.accessKeyId?.trim() && !isEditing) {
        errors.accessKeyId = 'Access key ID is required';
      }
      if (!formData.secretAccessKey?.trim() && !isEditing) {
        errors.secretAccessKey = 'Secret access key is required';
      }
    } else if (formData.serviceType === 'onedrive') {
      if (!formData.folderPath?.trim()) {
        errors.folderPath = 'Folder path is required';
      }
      if (!formData.clientId?.trim() && !isEditing) {
        errors.clientId = 'Client ID is required';
      }
      if (!formData.clientSecret?.trim() && !isEditing) {
        errors.clientSecret = 'Client secret is required';
      }
      if (!formData.refreshToken?.trim() && !isEditing) {
        errors.refreshToken = 'Refresh token is required';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildConfigDto = (): CreateArchivalConfigDto => {
    const baseDto: CreateArchivalConfigDto = {
      type: formData.serviceType,
      serviceConfig: {},
    };

    // Helper function to check if a field is a placeholder value
    const isPlaceholder = (value?: string) => value === '••••••••••••••••' || !value;

    if (formData.serviceType === 'azure-blob') {
      baseDto.serviceConfig = {
        storageAccountName: formData.storageAccount!,
        containerName: formData.containerName!,
        // Only include sasToken if it's not a placeholder (user entered new value)
        ...(isPlaceholder(formData.sasToken) ? {} : { sasToken: formData.sasToken }),
      };
    } else if (formData.serviceType === 's3') {
      baseDto.serviceConfig = {
        bucketName: formData.bucketName!,
        region: formData.region!,
        // Only include credentials if they're not placeholders
        ...(isPlaceholder(formData.accessKeyId) ? {} : { accessKeyId: formData.accessKeyId }),
        ...(isPlaceholder(formData.secretAccessKey)
          ? {}
          : { secretAccessKey: formData.secretAccessKey }),
      };
    } else if (formData.serviceType === 'onedrive') {
      baseDto.serviceConfig = {
        folderPath: formData.folderPath!,
        // Only include credentials if they're not placeholders
        ...(isPlaceholder(formData.clientId) ? {} : { clientId: formData.clientId }),
        ...(isPlaceholder(formData.clientSecret) ? {} : { clientSecret: formData.clientSecret }),
        ...(isPlaceholder(formData.refreshToken) ? {} : { refreshToken: formData.refreshToken }),
      };
    }

    return baseDto;
  };

  const handleTestConfiguration = async () => {
    if (!isEditing || !config?.serviceId) {
      setTestResult({
        success: false,
        message:
          'Please save the configuration first before testing. Testing requires a saved configuration.',
      });
      return;
    }

    if (!validateForm()) return;

    setIsTestingConfig(true);
    setTestResult(null);

    testConfigMutation.mutate(
      { configId: config.serviceId },
      {
        onSuccess: result => {
          setTestResult({
            success: result.success,
            message:
              result.message || (result.success ? 'Configuration test successful!' : 'Test failed'),
          });
        },
        onError: (error: Error) => {
          setTestResult({ success: false, message: error.message });
        },
        onSettled: () => {
          setIsTestingConfig(false);
        },
      }
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const configDto = buildConfigDto();

    if (isEditing && config) {
      updateConfigMutation.mutate(
        {
          tenantId: config.tenantId,
          serviceId: config.serviceId,
          data: configDto,
        },
        {
          onSuccess: () => {
            onSuccess();
          },
        }
      );
    } else {
      createConfigMutation.mutate(configDto, {
        onSuccess: () => {
          onSuccess();
        },
      });
    }
  };

  const renderSecretInput = (
    field: keyof FormData,
    label: string,
    placeholder: string,
    value?: string
  ) => {
    const isVisible = showSecrets[field];
    const hasError = !!validationErrors[field];

    return (
      <div>
        <label className="block text-sm font-medium text-xtnd-dark-600 mb-1">{label}</label>
        <div className="relative">
          <Input
            type={isVisible ? 'text' : 'password'}
            placeholder={isEditing ? '••••••••' : placeholder}
            value={value || ''}
            onChange={e => handleInputChange(field, e.target.value)}
            className={hasError ? 'border-red-300' : ''}
          />
        </div>
        {hasError && <p className="mt-1 text-sm text-red-600">{validationErrors[field]}</p>}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-xtnd-dark-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-xtnd-dark-800 border-b border-xtnd-dark-100 dark:border-xtnd-dark-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
                {isEditing ? 'Edit Configuration' : 'Add Archival Service'}
              </h2>
              <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-200 mt-1">
                {isEditing
                  ? 'Update the configuration for your archival service'
                  : 'Configure a new external storage service for invoice archival'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-xtnd-dark-300 hover:text-xtnd-dark-500 dark:hover:text-xtnd-dark-200 transition-colors"
            >
              <Icon name="HiX" size="lg" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Service Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="serviceType"
                className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1"
              >
                Service Type
              </label>
              <select
                id="serviceType"
                value={formData.serviceType}
                onChange={e =>
                  handleInputChange('serviceType', e.target.value as ArchivalServiceType)
                }
                disabled={isEditing}
                className="w-full px-3 py-2 border border-xtnd-dark-200 dark:border-xtnd-dark-600 rounded-md shadow-sm focus:ring-xtnd-blue focus:border-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
              >
                <option value="azure-blob">Azure Blob Storage</option>
                <option value="s3">AWS S3</option>
                <option value="onedrive">OneDrive</option>
              </select>
            </div>
          </div>

          {/* Service-specific Configuration */}
          {formData.serviceType === 'azure-blob' && (
            <Card>
              <CardHeader>
                <h3 className="font-medium text-xtnd-dark dark:text-xtnd-white">
                  Azure Blob Storage Configuration
                </h3>
              </CardHeader>
              <CardBody className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="storageAccount"
                      className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1"
                    >
                      Storage Account
                    </label>
                    <Input
                      id="storageAccount"
                      placeholder="mystorageaccount"
                      value={formData.storageAccount || ''}
                      onChange={e => handleInputChange('storageAccount', e.target.value)}
                      className={validationErrors.storageAccount ? 'border-red-300' : ''}
                    />
                    {validationErrors.storageAccount && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {validationErrors.storageAccount}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="containerName"
                      className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1"
                    >
                      Container Name
                    </label>
                    <Input
                      id="containerName"
                      placeholder="invoices"
                      value={formData.containerName || ''}
                      onChange={e => handleInputChange('containerName', e.target.value)}
                      className={validationErrors.containerName ? 'border-red-300' : ''}
                    />
                    {validationErrors.containerName && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {validationErrors.containerName}
                      </p>
                    )}
                  </div>
                </div>

                {renderSecretInput(
                  'sasToken',
                  'SAS Token',
                  'sv=2024-11-04&ss=...',
                  formData.sasToken
                )}
              </CardBody>
            </Card>
          )}

          {formData.serviceType === 's3' && (
            <Card>
              <CardHeader>
                <h3 className="font-medium text-xtnd-dark dark:text-xtnd-white">
                  AWS S3 Configuration
                </h3>
              </CardHeader>
              <CardBody className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="bucketName"
                      className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1"
                    >
                      Bucket Name
                    </label>
                    <Input
                      id="bucketName"
                      placeholder="my-invoice-bucket"
                      value={formData.bucketName || ''}
                      onChange={e => handleInputChange('bucketName', e.target.value)}
                      className={validationErrors.bucketName ? 'border-red-300' : ''}
                    />
                    {validationErrors.bucketName && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {validationErrors.bucketName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="region"
                      className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1"
                    >
                      Region
                    </label>
                    <Input
                      id="region"
                      placeholder="us-east-1"
                      value={formData.region || ''}
                      onChange={e => handleInputChange('region', e.target.value)}
                      className={validationErrors.region ? 'border-red-300' : ''}
                    />
                    {validationErrors.region && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {validationErrors.region}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderSecretInput(
                    'accessKeyId',
                    'Access Key ID',
                    'AKIA...',
                    formData.accessKeyId
                  )}
                  {renderSecretInput(
                    'secretAccessKey',
                    'Secret Access Key',
                    'wJalrXUtnFEMI/K7MDENG...',
                    formData.secretAccessKey
                  )}
                </div>
              </CardBody>
            </Card>
          )}

          {formData.serviceType === 'onedrive' && (
            <Card>
              <CardHeader>
                <h3 className="font-medium text-xtnd-dark dark:text-xtnd-white">
                  OneDrive Configuration
                </h3>
              </CardHeader>
              <CardBody className="space-y-4">
                <div>
                  <label
                    htmlFor="folderPath"
                    className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1"
                  >
                    Folder Path
                  </label>
                  <Input
                    id="folderPath"
                    placeholder="/Invoices/Archive"
                    value={formData.folderPath || ''}
                    onChange={e => handleInputChange('folderPath', e.target.value)}
                    className={validationErrors.folderPath ? 'border-red-300' : ''}
                  />
                  {validationErrors.folderPath && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {validationErrors.folderPath}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderSecretInput(
                    'clientId',
                    'Client ID',
                    'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
                    formData.clientId
                  )}
                  {renderSecretInput(
                    'clientSecret',
                    'Client Secret',
                    'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
                    formData.clientSecret
                  )}
                </div>

                {renderSecretInput(
                  'refreshToken',
                  'Refresh Token',
                  '0.AQcA...',
                  formData.refreshToken
                )}
              </CardBody>
            </Card>
          )}

          {/* Test Result */}
          {testResult && (
            <div
              className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}
            >
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <Icon name="HiCheckCircle" size="md" className="text-green-600" />
                ) : (
                  <Icon name="HiExclamationCircle" size="md" className="text-red-600" />
                )}
                <span
                  className={`font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}
                >
                  {testResult.success
                    ? 'Configuration Test Successful'
                    : 'Configuration Test Failed'}
                </span>
              </div>
              <p
                className={`mt-1 text-sm ${testResult.success ? 'text-green-700' : 'text-red-700'}`}
              >
                {testResult.message}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between pt-6 border-t border-xtnd-dark-100">
            <Button
              type="button"
              onClick={handleTestConfiguration}
              variant="outline"
              leftIcon={
                isTestingConfig ? (
                  <Icon name="LoadingSpinner" size="sm" />
                ) : (
                  <Icon name="HiCloudUpload" size="sm" />
                )
              }
              disabled={isTestingConfig || createOrUpdateMutation.isPending}
            >
              {isTestingConfig ? 'Testing...' : 'Test Configuration'}
            </Button>

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                disabled={createOrUpdateMutation.isPending}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="primary"
                leftIcon={
                  createOrUpdateMutation.isPending ? (
                    <Icon name="LoadingSpinner" size="lg" />
                  ) : undefined
                }
                disabled={createOrUpdateMutation.isPending || isTestingConfig}
              >
                {createOrUpdateMutation.isPending
                  ? isEditing
                    ? 'Updating...'
                    : 'Creating...'
                  : isEditing
                    ? 'Update Configuration'
                    : 'Create Configuration'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
